import React, { memo, ReactNode } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Stack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { CategoryPill, CategoryKey } from "@UI/Components/CategoryPill/CategoryPill";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { CircleCheck } from "@UI/Components/Icons/CircleCheck";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import messages from "../../messages";
import { Clock } from "@UI/Components/Icons/Clock";

export type TTaskCreationSummaryStepStatus = "completed" | "inProgress" | "pending";

export interface ITaskCreationSummaryStep {
  key: string;
  /** Absent for a config step with no icon of its own — the row still renders,
   * without one, rather than the panel breaking on an unknown step key. */
  icon?: React.ElementType;
  label: ReactNode;
  status: TTaskCreationSummaryStepStatus;
}

export interface ITaskCreationSummaryCategory {
  categoryKey: CategoryKey;
  name: string;
  subCategoryName?: string;
}

export interface ITaskCreationSummaryProps {
  /** Overrides the default "Your request" heading — the resume modal uses this
   * to show its own question instead of a second, redundant heading. */
  title?: ReactNode;
  /** Absent while the category tree is still resolving — the step rows show
   * on their own rather than the panel waiting on a pill. */
  category?: ITaskCreationSummaryCategory;
  steps: ITaskCreationSummaryStep[];
  /**
   * Renders a back link above the heading. Passed by the desktop panel only:
   * the mobile modal and the resume modal are dismissed with their own close
   * button, so a second "back" control there would mean two ways out.
   */
  onBack?: () => void;
  /**
   * Rendered beside the heading. The "Draft saved" pill today — a container
   * concern, since only a container knows when a save landed.
   */
  badge?: ReactNode;
  /** Rendered below the step rows. Empty by default (today's panel/mobile-modal
   * usage); containers that need CTAs (e.g. the resume modal) pass them here. */
  children?: ReactNode;
}

const statusMessageKeyByStatus: Record<TTaskCreationSummaryStepStatus, keyof typeof messages> = {
  completed: "taskCreation.step.status.completed",
  inProgress: "taskCreation.step.status.inProgress",
  pending: "taskCreation.step.status.pending",
};

const statusColorByStatus: Record<TTaskCreationSummaryStepStatus, string> = {
  completed: "green.500",
  inProgress: "green.500",
  pending: "gray.800",
};

const StepStatusIcon = ({ status }: { status: TTaskCreationSummaryStepStatus }) => {
  // A pending step gets no icon: it is dimmed and labelled "pending" already,
  // and a cross read as "failed" rather than "not yet".
  if (status === "pending") return null;
  if (status === "inProgress") {
    return (
      <Icon as={Clock} color={statusColorByStatus.inProgress} boxSize="16px" />
    );
  }
  return <Icon as={CircleCheck} color={statusColorByStatus.completed} boxSize="16px" />;
};

const StepRow = ({ icon, label, status }: ITaskCreationSummaryStep) => (
  <Flex align="center" gap="16px" opacity={status === "pending" ? 0.5 : 1}>
    <Flex
      align="center"
      justify="center"
      boxSize="42px"
      borderRadius="md"
      bg="gray.100"
      flexShrink={0}
    >
      {icon && <Icon as={icon} color="gray.700" boxSize="16px" />}
    </Flex>
    <Box flex="1">
      <Text fontWeight="bold" fontSize="md" color="gray.800">
        {label}
      </Text>
      <Text fontSize="sm" color={statusColorByStatus[status]}>
        <FormattedMessage {...messages[statusMessageKeyByStatus[status]]} />
      </Text>
    </Box>
    <StepStatusIcon status={status} />
  </Flex>
);

/**
 * The user's progress through the creation flow (RT-4321): category plus a row
 * per step with its answer status. One body, reused by whichever container
 * needs it — a panel, a modal, the draft-recovery modal — with no modal/layout
 * awareness of its own. CTAs are a container concern, passed in via `children`.
 */
export const TaskCreationSummary = memo(
  ({
    title,
    category,
    steps,
    onBack,
    badge,
    children,
  }: ITaskCreationSummaryProps) => (
    <Box>
      {onBack && (
        <Box marginBottom="24px">
          <BackLink onClick={onBack} />
        </Box>
      )}
      <Flex align="center" justify="space-between" gap="8px" marginBottom="24px">
        <Heading as="h2" fontSize="xl">
          {title ?? (
            <FormattedMessage {...messages["taskCreation.summary.title"]} />
          )}
        </Heading>
        {badge}
      </Flex>
      {category && (
        <Box marginBottom="16px">
          <CategoryPill
            categoryKey={category.categoryKey}
            name={
              category.subCategoryName
                ? `${category.name} > ${category.subCategoryName}`
                : category.name
            }
          />
        </Box>
      )}
      <Stack spacing="16px" divider={<Box borderBottom="1px" borderColor="gray.100" />}>
        {steps.map((step) => (
          <StepRow key={step.key} icon={step.icon} label={step.label} status={step.status} />
        ))}
      </Stack>
      {children && <Box marginTop="24px">{children}</Box>}
    </Box>
  )
);

TaskCreationSummary.displayName = "TaskCreationSummary";
